import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

// Styles & UI
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import { Col, Row, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from "reactstrap"; 
import { ToastContainer, toast } from "react-toastify";
import axios from "axios"; 

// Components
import TableContainer from '../../components/Common/TableContainer';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import Spinners from "components/Common/Spinner";

// Actions
import {
    getEvaluations as onGetEvaluations,
    getSessions as onGetSessions,
    runAnnualDeliberation as onRunAnnualDeliberation
} from "store/actions";

const SessionExamList = () => {
    const { sessionUuid } = useParams(); 
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [modalDelib, setModalDelib] = useState(false);
    
    // Récupération des états du sessionReducer
    const { 
        sessions, 
        annualDeliberationSuccess, 
        annualDeliberationData,
        loading: isProcessing 
    } = useSelector((state) => state.sessionReducer);

    const toggleModal = () => setModalDelib(!modalDelib);

    // --- SÉLECTEURS ---
    const currentSession = sessions.find((s) => s.uuid === sessionUuid);

    const selectEvaluationData = createSelector(
        (state) => state.evaluationReducer,
        (reducer) => ({
            evaluations: reducer.evaluations,
            loading: reducer.loading,
            annualDeliberation: reducer.annualDeliberation 
        })
    );
    const { evaluations, loading, annualDeliberation } = useSelector(selectEvaluationData);

    // --- LOGIQUE D'AFFICHAGE DYNAMIQUE ---
    const isDeliberated = useMemo(() => {
        const isDoneInDB = annualDeliberation && (annualDeliberation.done === true || annualDeliberation.done === 1);
        return isDoneInDB || annualDeliberationSuccess;
    }, [annualDeliberation, annualDeliberationSuccess]);

    const delibNavData = useMemo(() => {
        return annualDeliberationSuccess ? annualDeliberationData : annualDeliberation;
    }, [annualDeliberationSuccess, annualDeliberationData, annualDeliberation]);

    // --- LOGIQUE DE DÉLIBÉRATION CORRIGÉE (SESSION 1 & 2) ---
    const canProcessAnnualDeliberation = useMemo(() => {
        if (!evaluations || evaluations.length === 0 || !currentSession) return false;

        const sessionTitle = currentSession.title?.toUpperCase() || "";
        const sessionType = currentSession.type?.toUpperCase() || "";

        console.log("===SESSION TYPE: ", sessionType);
        console.log("===SESSION TITLE: ", sessionTitle);

        // Détection de la Session 2 / Rattrapage
        const isSession2 = sessionType === "RATTRAPAGE" || 
                          sessionTitle.includes("SESSION 2") ||
                          sessionTitle.includes("RATTRAPAGE");

        // Tous les examens listés doivent être proclamés pour permettre la délibération finale
        //const allProclaimed = evaluations.every(ex => ex.status === 'PROCLAIMED');
        const allProclaimed = true;

        if (isSession2) {
            console.log("=====IS SESSION 2:", isSession2);
            // En Session 2 : On peut délibérer dès qu'il y a au moins un examen (le rattrapage) et qu'il est proclamé

            console.log("====PROCLAMED: ", allProclaimed);
            return allProclaimed;
        } else {
            // En Session 1 : On garde la règle des 2 semestres (S1 et S2)
            const hasEnoughExams = evaluations.length >= 2;
            return hasEnoughExams && allProclaimed;
        }
    }, [evaluations, currentSession]);

    // Effet pour fermer le modal dès que le succès est confirmé
    useEffect(() => {
        if (annualDeliberationSuccess && modalDelib) {
            setModalDelib(false);
        }
    }, [annualDeliberationSuccess, modalDelib]);

    // --- ACTION DE LANCEMENT ---
    const handleRunDeliberation = () => {
        dispatch(onRunAnnualDeliberation({ session_uuid: sessionUuid }));
    };

    // --- APPEL API ---
    useEffect(() => {
        if (sessionUuid) {
            dispatch(onGetEvaluations({ session_uuid: sessionUuid }));
        }
        if (sessions.length === 0) dispatch(onGetSessions());
    }, [dispatch, sessionUuid, sessions.length]);

    // --- COLONNES ---
    const columns = useMemo(() => [
        {
            header: "Titre",
            accessorKey: "title",
            enableColumnFilter: false,
            cell: (cell) => <span className="fw-medium">{cell.getValue()}</span>
        },
        {
            header: "Statut",
            accessorKey: "status",
            enableColumnFilter: false,
            cell: (cell) => {
                const status = cell.getValue();
                let badgeClass = "badge-soft-secondary";
                if (status === 'PROCLAIMED') badgeClass = "badge-soft-success";
                if (status === 'GRADING') badgeClass = "badge-soft-warning";
                return <span className={`badge ${badgeClass} font-size-12`}>{status}</span>;
            }
        },
        {
            header: 'Actions',
            cell: (cellProps) => {
                const item = cellProps.row.original;
                return (
                    <button
                        type="button"
                        className="btn btn-soft-primary btn-sm"
                        onClick={() => navigate(`/session/${sessionUuid}/exam/${item.uuid}/results`)}
                    >
                        <i className="bx bx-bar-chart-alt-2 me-1"></i> Gérer Résultats
                    </button>
                );
            }
        },
    ], [sessionUuid, navigate]);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs 
                        title="Sessions" 
                        breadcrumbItem={currentSession ? 'Examens' : "Chargement..."} 
                    />
                    
                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardBody className="border-bottom">
                                    <div className="d-flex align-items-center">
                                        <h5 className="mb-0 card-title flex-grow-1">
                                            {currentSession ? (
                                                <>Session : <span className="text-primary">{currentSession.title}</span></>
                                            ) : "Récupération..."}
                                        </h5> 

                                        <div className="flex-shrink-0 d-flex gap-2">
                                            {/* Visibles si la délibération est déjà effectuée */}
                                            {isDeliberated && delibNavData && (
                                                <div className="d-flex gap-2 animate__animated animate__fadeIn">
                                                    <Button 
                                                        color="primary" 
                                                        size="sm"
                                                        onClick={() => navigate(`/final-decisions/session/${sessionUuid}`)}
                                                    >
                                                        <i className="bx bx-list-check me-1"></i> Résultats délibérations
                                                    </Button>
                                                    <Button 
                                                        color="warning" 
                                                        size="sm"
                                                        className="text-white"
                                                        onClick={() => navigate(`/final-decisions/session/${sessionUuid}/retest-candidates`)}
                                                    >
                                                        <i className="bx bx-time-five me-1"></i> Liste de rattrapage
                                                    </Button>
                                                </div>
                                            )}

                                            {/* Bouton de lancement de la délibération */}
                                            {canProcessAnnualDeliberation && !isDeliberated && (
                                                <Button 
                                                    color="success" 
                                                    size="sm"
                                                    onClick={toggleModal}
                                                >
                                                    <i className="bx bx-award me-1"></i>
                                                    Déliberation de fin d'année
                                                </Button>
                                            )}

                                            <button 
                                                className="btn btn-light btn-sm shadow-none border"
                                                onClick={() => navigate("/sessions")}
                                            >
                                                <i className="mdi mdi-arrow-left me-1"></i> Liste des sessions
                                            </button>
                                        </div>
                                    </div>
                                </CardBody>
                                <CardBody>
                                    {loading ? (
                                        <div className="py-5 text-center">
                                            <Spinners setLoading={setIsLoading} />
                                        </div>
                                    ) : (
                                        <TableContainer
                                            columns={columns}
                                            data={evaluations || []}
                                            isCustomPageSize={true}
                                            isGlobalFilter={true}
                                            isPagination={true}
                                            SearchPlaceholder="Rechercher..."
                                            tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                                            theadClass="table-light"
                                            pagination="pagination"
                                            paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                                        />
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            <Modal isOpen={modalDelib} toggle={toggleModal} centered>
                <ModalHeader toggle={toggleModal}>
                    <i className="bx bx-error text-warning me-2"></i>
                    Confirmation de Délibération
                </ModalHeader>
                <ModalBody>
                    <Alert color="info" className="font-size-13">
                        <i className="bx bx-info-circle me-1"></i>
                        Cette action va calculer les résultats définitifs pour cette session.
                    </Alert>
                    <p>Le système va appliquer les règles FMOS pour valider l'année ou le cycle (Note ≥ 10/20, passage avec dettes, etc.).</p>
                    <p className="mb-0 fw-bold">Voulez-vous continuer ?</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal} disabled={isProcessing}>Annuler</Button>
                    <Button color="success" onClick={handleRunDeliberation} disabled={isProcessing}>
                        {isProcessing ? <><i className="bx bx-loader bx-spin me-1"></i> Calcul...</> : "Lancer la délibération"}
                    </Button>
                </ModalFooter>
            </Modal>
            <ToastContainer />
        </React.Fragment>
    );
};

export default SessionExamList;